import { useState, type FormEvent } from "react";
import { Mail, MapPin, Phone } from "lucide-react";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState<string>("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    // Honeypot check
    const trap = String(formData.get("website_alt") || "").trim();
    if (trap) {
      // Silent success-like feedback
      setStatus("success");
      setFormError("");
      formEl.reset();
      return;
    }
    // Do not include honeypot field in submission
    formData.delete("website_alt");

    // Textarea character limit
    const message = String(formData.get("message") || "");
    if (message.length > 500) {
      setStatus("error");
      setFormError("문의 내용은 500자 이내로 작성해주세요.");
      return;
    }

    setStatus("submitting");
    setFormError("");
    try {
      const payload: Record<string, string> = {};
      formData.forEach((value, key) => {
        payload[key] = String(value);
      });
      payload._subject = "[LRGS1 X9 PRO] 새 고객 문의";
      payload._template = "table";
      payload._captcha = "false";

      const response = await fetch("https://formsubmit.co/ajax/nixnix111@naver.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let parsed: {
          success?: boolean | string;
          message?: string;
        } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = String(
        parsed?.message || responseText || ""
      );

      if (response.ok && parsed?.success !== false) {
        setStatus("success");
        setFormError("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "문의 접수에 실패했습니다. 잠시 후 다시 시도해주세요.");
      }
    } catch (err) {
      setStatus("error");
      setFormError("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    <section id="contact" className="w-full bg-background-950 py-14 md:py-24 text-background-50">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 md:gap-12 lg:gap-16">
          <div>
            <span className="inline-block font-heading tracking-[0.32em] text-xs font-semibold text-primary-400">
              CONTACT US
            </span>
            <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold leading-tight tracking-tight">
              라이더의 이야기를<br />기다립니다.
            </h2>
            <p className="mt-5 text-background-200 leading-relaxed max-w-md">
              대량 구매, 오프라인 매장 입점, 미디어 협업, 제품 관련 문의 등
              무엇이든 편하게 남겨주세요. 영업일 기준 24시간 안에 답변드립니다.
            </p>

            <ul className="mt-10 space-y-5">
              {[
                {
                  icon: Phone,
                  label: "고객센터",
                  value: (
                    <>
                      <span className="block whitespace-nowrap">032-668-8723 내선 1번</span>
                      <span className="block whitespace-nowrap">평일 10:00 – 17:00</span>
                    </>
                  ),
                },
                { icon: Mail, label: "이메일", value: "nixnix111@naver.com" },
                { icon: MapPin, label: "본사", value: "인천광역시 부평구 경인로 1083" },
              ].map((it) => (
                <li key={it.label} className="flex items-start gap-4">
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950">
                    <it.icon size={20} strokeWidth={2.25} aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs tracking-widest font-heading text-background-300">
                      {it.label}
                    </div>
                    <div className="mt-1 text-sm md:text-base text-background-50 leading-relaxed break-keep">
                      {it.value}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl bg-background-900 border border-background-800/60 p-5 md:p-10">
            {status === "success" ? (
              <div className="flex flex-col items-center text-center py-14">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500 text-background-50 dark:text-foreground-950">
                  <i className="ri-check-line text-3xl"></i>
                </span>
                <h3 className="mt-6 font-heading text-2xl font-bold">문의가 접수되었어요</h3>
                <p className="mt-3 text-background-200 max-w-sm">
                  영업일 기준 24시간 안에 담당자가 이메일로 답변드리겠습니다.
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-8 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 px-5 py-2.5 text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  새 문의 남기기
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="ridevox-contact-form"
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                <div className="md:col-span-2">
                  <label className="block text-xs tracking-widest font-heading text-background-300 mb-2">
                    문의 유형
                  </label>
                  <select
                    name="inquiry_type"
                    required
                    defaultValue=""
                    className="w-full rounded-md bg-background-800 border border-background-700 text-background-50 px-4 py-3 text-sm focus:outline-none focus:border-primary-500 cursor-pointer"
                  >
                    <option value="" disabled>선택해주세요</option>
                    <option value="product">제품 문의</option>
                    <option value="bulk">대량 구매</option>
                    <option value="store">오프라인 매장 입점</option>
                    <option value="media">미디어 / 협업</option>
                    <option value="etc">기타</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs tracking-widest font-heading text-background-300 mb-2">
                    이름
                  </label>
                  <input
                    name="name"
                    required
                    placeholder="홍길동"
                    className="w-full rounded-md bg-background-800 border border-background-700 text-background-50 placeholder-background-500 px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-widest font-heading text-background-300 mb-2">
                    이메일
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md bg-background-800 border border-background-700 text-background-50 placeholder-background-500 px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-widest font-heading text-background-300 mb-2">
                    연락처
                  </label>
                  <input
                    name="phone"
                    placeholder="010-0000-0000"
                    className="w-full rounded-md bg-background-800 border border-background-700 text-background-50 placeholder-background-500 px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-widest font-heading text-background-300 mb-2">
                    소속 (선택)
                  </label>
                  <input
                    name="company"
                    placeholder="회사명 또는 라이딩 크루"
                    className="w-full rounded-md bg-background-800 border border-background-700 text-background-50 placeholder-background-500 px-4 py-3 text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs tracking-widest font-heading text-background-300 mb-2">
                    문의 내용
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="문의하실 내용을 자세히 알려주세요. (최대 500자)"
                    className="w-full rounded-md bg-background-800 border border-background-700 text-background-50 placeholder-background-500 px-4 py-3 text-sm focus:outline-none focus:border-primary-500 resize-none"
                  ></textarea>
                </div>

                {/* Honeypot */}
                <div className="rv-honeypot" aria-hidden="true">
                  <label>
                    회사 홈페이지
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      readOnly
                      aria-hidden="true"
                    />
                  </label>
                </div>

                <div className="md:col-span-2 flex items-start gap-3 mt-2">
                  <input
                    id="agree"
                    type="checkbox"
                    name="agree_privacy"
                    required
                    value="agreed"
                    className="mt-1 accent-primary-500 cursor-pointer"
                  />
                  <label
                    htmlFor="agree"
                    className="text-xs text-background-300 cursor-pointer"
                  >
                    개인정보 수집 및 이용에 동의합니다. 수집된 정보는 문의 응대
                    목적으로만 사용되며, 3개월 후 파기됩니다.
                  </label>
                </div>

                {status === "error" && (
                  <div className="md:col-span-2 rounded-md border border-primary-500 bg-primary-500/10 px-4 py-3 text-sm text-primary-200">
                    <i className="ri-error-warning-line mr-2"></i>
                    {formError || "문의 접수에 실패했습니다. 잠시 후 다시 시도해주세요."}
                  </div>
                )}

                <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-2">
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-70 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 px-6 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        전송 중...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        문의 보내기
                      </>
                    )}
                  </button>
                  <span className="text-xs text-background-400">
                    영업일 기준 24시간 내 답변드립니다.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
